import { useEffect } from 'react';

import { Route } from 'react-router';
import { useAuth } from 'react-oidc-context';

import SystemAdministration from '@legacy/screens/SystemAdministration';
import PageSettings from '@legacy/screens/PageSettings';
import Home from '@legacy/screens/Home';
import PersonalSettings from '@legacy/screens/PersonalSettings';
import LayoutPage from '@legacy/components/layout/LayoutPage';
import { LegacyPage } from '@legacy/components/layout/LegacyPage';
import TimeTrackerTestComponent from '@legacy/utils/time/timeTrackerTime/TimeTrackerTestComponent';
import { PageRoute } from '@legacy/routers/PageRoute';
import LegacyRoute from '@legacy/routers/LegacyRoute';
import SamplePathRoute from '@legacy/routers/SamplePathRoute';
import { isDev } from '@legacy/utils/env';
import {
  systemAdministrationRoutes,
  personalSettingsRoutes,
  designSixPageSettingsRoutes,
  editProjectTemplateRoute,
} from '@legacy/constants/paths';
import { designSevenPagePath } from '@legacy/designSevenPagePath';
import AuthRoute from '../AuthRoute';
import ProjectsRouter from '../ProjectsRouter';
import { ReportsRouter } from '../ReportsRouter';
import { EmployeesRouter } from '../EmployeesRouter';
import { PageSettingsRouter } from '../PageSettingsRouter';
import { handleLogout } from '@/routes/LogoutRoute';

export const administrativeRoutes = systemAdministrationRoutes
  .concat(personalSettingsRoutes)
  .concat(designSixPageSettingsRoutes);

const LogOff = () => {
  const auth = useAuth();
  useEffect(() => {
    handleLogout(auth);
  }, []);

  return null;
};

export const MainRouter = (pathname: string) => {
  return [
    ...(personalSettingsRoutes || []).map((url) => (
      <Route
        key={url}
        path={url}
        element={
          <AuthRoute>
            <PersonalSettings />
          </AuthRoute>
        }
      />
    )),

    //  Must be put before system administration route for edit project template
    <Route
      key={editProjectTemplateRoute}
      path={editProjectTemplateRoute}
      element={
        <AuthRoute>
          <LayoutPage />
        </AuthRoute>
      }
    >
      <Route
        path="*"
        element={<LegacyPage data-automation-id="LegacyPage" />}
      />
    </Route>,

    ...(systemAdministrationRoutes || []).map((url) => (
      <Route
        key={url}
        path={url}
        element={
          <AuthRoute>
            <SystemAdministration />
          </AuthRoute>
        }
      />
    )),

    ...(designSixPageSettingsRoutes || []).map((url) => (
      <Route
        key={url}
        path={url}
        element={
          <AuthRoute>
            <PageSettings />
          </AuthRoute>
        }
      />
    )),

    <Route
      key={designSevenPagePath.pageSettings.root}
      path={designSevenPagePath.pageSettings.root}
      element={<PageSettingsRouter />}
    />,

    //  Route for log out
    <Route key={'logoff'} path="/logoff" element={<LogOff />} />,
    <Route
      key={'index'}
      element={
        <AuthRoute>
          {/* 
            We need display: inline-block for the table to overflow horizontally.
            Once RP is migrated to use MUI, we can remove this, as it will not overflow. 
          */}
          <LayoutPage
            hasCustomTable={pathname === '/projects/resource-planner'}
          />
        </AuthRoute>
      }
    >
      <Route
        path={designSevenPagePath.home}
        element={
          <PageRoute
            pageTitle="Home"
            pageId="5f11ec8e-27f0-47b2-9bb2-667ed9f4800c"
            pageIdentifier="DesignSevenFrontPage"
          >
            <Home />
          </PageRoute>
        }
      />

      <Route
        path={designSevenPagePath.reports.root}
        element={<ReportsRouter />}
      />

      {...ProjectsRouter()}

      <Route
        path={designSevenPagePath.employees.root}
        element={<EmployeesRouter />}
      />

      {/* // Development routes */}
      {...isDev
        ? [
            <Route
              key="time-tracker-test"
              path="/test/utils/time/timeTrackerTime"
              element={<TimeTrackerTestComponent />}
            />,
            <Route
              key="sample-path"
              path={designSevenPagePath.sample.root}
              element={<SamplePathRoute />}
            />,
          ]
        : []}

      <Route
        path="*"
        element={
          <LegacyRoute>
            <LegacyPage data-automation-id="LegacyPage" />
          </LegacyRoute>
        }
      />
    </Route>,
  ];
};
